import AgoraRTC from "agora-rtc-react";
import axios from 'axios';

class AgoraService {
    constructor() {
        // Add these initializations
        this._volumeMonitors = new Map();
        this.remoteAudioLevels = new Map();
        // Core Agora client setup with global coverage
        this.client = AgoraRTC.createClient({ 
            mode: "rtc",
            codec: "vp8",
            areaCode: [
                AgoraRTC.AREAS.GLOBAL,          
                AgoraRTC.AREAS.ASIA,            
                AgoraRTC.AREAS.NORTH_AMERICA,   
                AgoraRTC.AREAS.EUROPE,          
                AgoraRTC.AREAS.JAPAN,           
                AgoraRTC.AREAS.INDIA,           
                AgoraRTC.AREAS.CHINA,           
                AgoraRTC.AREAS.OCEANIA,         
                AgoraRTC.AREAS.SOUTH_AMERICA,   
                AgoraRTC.AREAS.AFRICA           
            ]
        });

        // Main state variables
        this.localAudioTrack = null;
        this.appId = process.env.REACT_APP_AGORA_APP_ID;
        this.isInitialized = false;
        this.currentChannel = null;
        this._isMuted = false;
        this.audioContext = null;
        this._audioCheckInterval = null;
        this.isCleaningUp = false;
        
        // Enhanced state tracking
        this.connectionState = {
            status: 'disconnected',
            lastConnected: null,
            reconnectAttempts: 0,
            maxReconnectAttempts: 3
        };

        this.audioState = {
            isEnabled: false,
            volume: 0,
            quality: 'unknown',
            lastCheck: null
        };

        // Timing variables
        this.lastPing = null;
        this.connectionStartTime = null;
        this.PING_INTERVAL = 3000;
        this.RECONNECT_TIMEOUT = 15000;
        this.AUDIO_CHECK_INTERVAL = 2000;

        // Enable volume indicators
        if (this.client) {
            console.log('Enabling volume indicator in constructor');
            this.client.enableAudioVolumeIndicator({
                interval: 1000,
                smooth: 3,
                enableVad: true
            });
        }

        // // Platform-specific setup
        // this.isIOS = this.checkIfIOS();
        // if (this.isIOS) {
        //     this.setupIOSAudioContext();
        // }

        // Bind methods
        this.handleConnectionStateChange = this.handleConnectionStateChange.bind(this);
        this.handleUserPublished = this.handleUserPublished.bind(this);
        this.handleUserUnpublished = this.handleUserUnpublished.bind(this);
        this.handleException = this.handleException.bind(this);

        // Initialize event listeners
        this.setupEventListeners();
        this.setupAutoRestoration();
    }

    async getLocalAudioLevel() {
        try {
            if (!this.localAudioTrack) {
                return 0;
            }
            
            // Get the native MediaStreamTrack
            const mediaStreamTrack = this.localAudioTrack.getMediaStreamTrack();
            if (!mediaStreamTrack) {
                return 0;
            }
    
            // Create audio context for volume measurement
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const mediaStreamSource = audioContext.createMediaStreamSource(new MediaStream([mediaStreamTrack]));
            const analyser = audioContext.createAnalyser();
            mediaStreamSource.connect(analyser);
    
            // Configure analyser
            analyser.fftSize = 256;
            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);
    
            // Get volume data
            analyser.getByteFrequencyData(dataArray);
    
            // Calculate average volume
            const average = dataArray.reduce((acc, value) => acc + value, 0) / bufferLength;
            
            // Normalize to 0-1 range
            const normalizedVolume = average / 255;
    
            // Cleanup
            await audioContext.close();
            
            return normalizedVolume;
        } catch (error) {
            console.error('Error getting audio level:', error);
            return 0;
        }
    }

    async setLocalVolume(level) {
        try {
            if (this.localAudioTrack) {
                console.log('Setting local volume:', level);
                await this.localAudioTrack.setVolume(level);
                
                // Add immediate volume check
                const currentVolume = await this.localAudioTrack.getVolumeLevel();
                console.log('Volume after setting:', currentVolume);
                
                this.audioState = {
                    ...this.audioState,
                    volume: level
                };
                console.log('Updated audioState:', this.audioState);
            } else {
                console.warn('No local audio track available for volume adjustment');
            }
        } catch (error) {
            console.error('Error setting local volume:', error, error.stack);
            throw error;
        }
    }

    async setRemoteVolume(uid, level) {
        try {
            const remoteUser = this.client?.remoteUsers.find(user => user.uid === uid);
            if (remoteUser?.audioTrack) {
                await remoteUser.audioTrack.setVolume(level);
                console.log(`Remote user ${uid} volume set to: ${level}`);
                
                // Update remote audio levels map
                this.remoteAudioLevels.set(uid, level);
            } else {
                console.warn(`No audio track found for remote user ${uid}`);
            }
        } catch (error) {
            console.error(`Error setting volume for remote user ${uid}:`, error);
            throw error;
        }
    }

    async setupLocalAudio() {
        try {
            console.log('Setting up local audio...');
            
            // Clear existing volume monitoring interval if any
            if (this._volumeMonitorInterval) {
                clearInterval(this._volumeMonitorInterval);
            }
    
            // Clean up existing track if any
            if (this.localAudioTrack) {
                try {
                    await this.localAudioTrack.setEnabled(false);
                    await this.localAudioTrack.close();
                } catch (e) {
                    console.warn('Error cleaning up existing track:', e);
                }
                this.localAudioTrack = null;
            }
    
            // Configure audio settings for optimal voice call
            const audioConfig = {
                encoderConfig: {
                    sampleRate: 32000,    // Increased for better quality
                    stereo: false,
                    bitrate: 48,         // Optimized for voice
                },
                AEC: true,
                AGC: true,
                ANS: true,
                enableInbandFec: true,    // Forward error correction
                enableDtx: true,          // Discontinuous transmission
                enableRedundancy: true    // Audio redundancy
            };
    
            // Create local audio track with enhancement
            this.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack(audioConfig);
    
            // Explicit track enablement
            await this.localAudioTrack.setEnabled(true);
    
            // Wait for track to stabilize
            await new Promise(resolve => setTimeout(resolve, 100));
    
            // Verify track state
            const trackState = this.localAudioTrack.getMediaStreamTrack().readyState;
            console.log('Audio track state after setup:', trackState);
    
            // Set initial volume
            console.log('Setting initial volume...');
            await this.localAudioTrack.setVolume(100);
    
            // Verify volume level
            const currentVolume = this.localAudioTrack.getVolumeLevel();
            console.log('Initial volume level:', currentVolume);
    
            // Setup volume monitoring internally
            this._volumeMonitorInterval = setInterval(() => {
                if (this.localAudioTrack?.enabled) {
                    const volume = this.localAudioTrack.getVolumeLevel();
                    if (volume === 0) {
                        console.warn('Zero volume detected, needs verification');
                        // Just log the warning, verifyAudioTrack will handle recovery if needed
                    }
                }
            }, 2000);
    
            // Log success state
            console.log('Audio setup completed:', {
                trackId: this.localAudioTrack.trackId,
                enabled: this.localAudioTrack.enabled,
                state: this.localAudioTrack.state,
                volume: currentVolume
            });
    
            // Return success only if all verifications pass
            return !!(this.localAudioTrack?.enabled &&
                     trackState === 'live' &&
                     this.localAudioTrack.getMediaStreamTrack().readyState === 'live');
    
        } catch (error) {
            console.error('Audio setup failed:', error);
            // Cleanup on failure
            if (this.localAudioTrack) {
                try {
                    await this.localAudioTrack.close();
                } catch (cleanupError) {
                    console.warn('Cleanup after failure error:', cleanupError);
                }
                this.localAudioTrack = null;
            }
            if (this._volumeMonitorInterval) {
                clearInterval(this._volumeMonitorInterval);
                this._volumeMonitorInterval = null;
            }
            return false;
        }
    }

    // Add this method to AgoraService class
async verifyAndRestoreAudio() {
    try {
        if (!this.localAudioTrack || !this.localAudioTrack.enabled) {
            await this.setupLocalAudio();
            return;
        }

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const tracks = stream.getAudioTracks();
        
        if (tracks.length === 0 || !tracks[0].enabled) {
            await this.setupLocalAudio();
        }

        tracks.forEach(track => track.stop());
    } catch (error) {
        console.error('Audio verification failed:', error);
        await this.setupLocalAudio();
    }
}

// Setup auto-restoration for interrupted audio
setupAutoRestoration() {
        ['click', 'touchstart', 'keydown'].forEach(eventType => {
            document.addEventListener(eventType, async () => {
                if (this.isInitialized && this.currentChannel) {
                    await this.verifyAndRestoreAudio();
                }
            }, { once: false });
        });
    }

async getCallStats() {
        try {
            if (!this.client || !this.currentChannel) {
                throw new Error('Client or channel not initialized');
            }
    
            // Get client and connection stats
            const rtcStats = await this.client.getRTCStats();
            const connectionStats = await this.client.getLocalAudioStats();
    
            return {
                latency: rtcStats.RTT || 0, // Round trip time
                packetLoss: rtcStats.RecvPacketLoss || 0,
                volume: connectionStats.sendVolumeLevel || 0,
                quality: {
                    upload: rtcStats.SendBitrate || 0,
                    download: rtcStats.RecvBitrate || 0
                },
                timestamp: Date.now()
            };
        } catch (error) {
            console.error('Error getting call stats:', error);
            // Return default values if stats can't be retrieved
            return {
                latency: 0,
                packetLoss: 0,
                volume: 0,
                quality: {
                    upload: 0,
                    download: 0
                },
                timestamp: Date.now()
            };
        }
    }
    
// Diagnostics
async diagnoseMissingAudio() {
    try {
        if (!this.client) {
            console.warn('Agora client not initialized');
            return null;
        }

        // Get current microphone access state
        const micPermission = await navigator.permissions.query({ name: 'microphone' });

        // Get real-time volume level first
        const currentVolume = this.localAudioTrack?.getVolumeLevel();
        console.log('Current captured volume:', currentVolume);

        const audioState = {
            clientState: this.client.connectionState,
            microphonePermission: micPermission.state,
            callInfo: {
                channelName: this.currentChannel,
                connectionDuration: this.connectionStartTime ? 
                    Date.now() - this.connectionStartTime : 0,
                networkQuality: this.connectionState.networkQuality
            }
        };

        // Use current volume in localTrack
        const localTrack = this.localAudioTrack;
        audioState.localTrack = {
            exists: !!localTrack,
            enabled: localTrack?.enabled || false,
            state: localTrack?.state || 'none',
            volume: currentVolume || 0  // Use captured volume
        };

        // Remote users info - we definitely need this!
        const remoteUsers = this.client.remoteUsers || [];
        audioState.remoteUsers = remoteUsers.map(user => ({
            uid: user.uid,
            hasAudio: !!user.audioTrack,
            audioEnabled: user.audioTrack?.enabled || false,
            volume: this.remoteAudioLevels.get(user.uid) || 0
        }));

        // Log full state
        console.log('Constructed audioState:', audioState);
        return audioState;
    } catch (error) {
        console.error('Error in audio diagnosis:', error);
        return null;
    }
}

    // Connection monitoring
    // In agoraService.js
startConnectionMonitoring() {
    if (this._monitoringInterval) {
        clearInterval(this._monitoringInterval);
    }

    this._monitoringInterval = setInterval(async () => {
        if (this.currentChannel) {
            try {
                // Get connection stats
                const stats = await this.client.getRTCStats();
                
                // Get audio diagnosis (this maintains the existing audio monitoring)
                const audioDiagnosis = await this.diagnoseMissingAudio();

                // Update connection state with both connection and audio metrics
                this.connectionState = {
                    ...this.connectionState,
                    lastCheck: Date.now(),
                    stats: {
                        receivedBytes: stats.RecvBytes || 0,
                        sentBytes: stats.SendBytes || 0,
                        receivedPackets: stats.RecvPackets || 0,
                        sentPackets: stats.SendPackets || 0,
                        rtt: stats.RTT || 0
                    },
                    audio: {
                        volume: audioDiagnosis?.localTrack?.volume || 0,
                        active: audioDiagnosis?.localTrack?.enabled || false,
                        exists: audioDiagnosis?.localTrack?.exists || false
                    },
                    isMonitoring: true
                };

            } catch (error) {
                console.error('Error monitoring connection:', error);
                this.connectionState = {
                    ...this.connectionState,
                    isMonitoring: false,
                    lastError: error.message
                };
            }
        }
    }, this.PING_INTERVAL);

    return true;
}

startClientMonitoring() {
    if (this.client) {

            // Start monitoring connection state
            this.startConnectionMonitoring();
            
            // Initialize monitoring states
            this._volumeMonitors = new Map();
            this.remoteAudioLevels = new Map();
            
            console.log('Client monitoring started');
        }
    }

// Platform specific setup
async setupIOSAudio() {
    try {
        if (this.audioContext) {
            await this.audioContext.close();
        }
        
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        
        const resumeAudio = async () => {
            if (this.audioContext?.state === 'suspended') {
                await this.audioContext.resume();
            }
        };

        ['touchstart', 'touchend', 'click'].forEach(event => {
            document.addEventListener(event, resumeAudio, { once: false });
        });

        const constraints = {
            audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
                sampleRate: 48000
            }
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        window.iosAudioStream = stream;

        return true;
    } catch (error) {
        console.error('iOS audio setup error:', error);
        throw error;
    }
}

// Audio verification
async verifyAudioTrack() {
    try {
        if (!this.localAudioTrack) {
            console.log('No audio track found, attempting setup...');
            await this.setupLocalAudio();
        }

        await new Promise(resolve => setTimeout(resolve, 100));

        const trackExists = !!this.localAudioTrack;
        const trackEnabled = this.localAudioTrack?.enabled ?? false;
        const trackState = this.localAudioTrack?.state ?? 'none';

        let volumeLevel = 0;
        try {
            volumeLevel = await this.getLocalAudioLevel();
            } catch (error) {
            console.warn('Could not get volume level:', error);
        }

        const trackStatus = {
            isValid: trackExists && trackEnabled && trackState !== 'stopped',
            details: {
                exists: trackExists,
                enabled: trackEnabled,
                state: trackState,
                volume: volumeLevel,
                muted: !trackEnabled
            }
        };

        console.log('Audio track verification details:', trackStatus);
        return trackStatus;
    } catch (error) {
        console.error('Audio track verification failed:', error);
        return { isValid: false, details: { error: error.message } };
    }
}

// Cleanup
async cleanup() {
    console.log('Starting Agora service cleanup');
    this.isCleaningUp = true;  // ADD: Set flag at start
    try {
        // Clear monitoring intervals safely
        const intervals = [
            this._monitoringInterval,
            ...Array.from(this._volumeMonitors.values())
        ];

        intervals.forEach(interval => {
            if (interval) clearInterval(interval);
        });

        this._monitoringInterval = null;
        this._volumeMonitors.clear();

        // Clean up audio track with safety checks
        if (this.localAudioTrack) {
            try {
                await this.localAudioTrack.setEnabled(false);
                await this.localAudioTrack.close();
            } catch (trackError) {
                console.warn('Error closing audio track:', trackError);
            }
            this.localAudioTrack = null;
        }

        // Clean up audio context safely
        if (this.audioContext) {
            try {
                if (this.audioContext.state !== 'closed') {
                    await this.audioContext.close();
                }
            } catch (contextError) {
                console.warn('Error closing audio context:', contextError);
            }
            this.audioContext = null;
        }

        // Clean up iOS stream safely
        if (window.iosAudioStream) {
            try {
                const tracks = window.iosAudioStream.getTracks();
                tracks.forEach(track => {
                    try {
                        track.stop();
                    } catch (trackError) {
                        console.warn('Error stopping iOS track:', trackError);
                    }
                });
            } catch (streamError) {
                console.warn('Error cleaning up iOS stream:', streamError);
            }
            window.iosAudioStream = null;
        }

        // Leave Agora channel safely
        if (this.client) {
            try {
                if (this.client.connectionState === 'CONNECTED') {
                    await this.client.leave();
                }
                // Additional client cleanup
                if (this.client.remoteUsers.length > 0) {
                    await Promise.all(
                        this.client.remoteUsers.map(user => 
                            this.client.unsubscribe(user)
                        )
                    );
                }
            } catch (clientError) {
                console.warn('Error during client cleanup:', clientError);
            }
        }

        // Reset all state
        this.currentChannel = null;
        this._isMuted = false;
        this.isInitialized = false;
        this.connectionStartTime = null;
        
        // Reset connection state
        this.connectionState = {
            status: 'disconnected',
            lastConnected: null,
            reconnectAttempts: 0,
            lastError: null
        };

        // Reset audio state
        this.audioState = {
            isEnabled: false,
            volume: 0,
            quality: 'unknown',
            lastCheck: null
        };

        // Clear monitoring maps
        this.remoteAudioLevels.clear();

        console.log('Agora service cleanup completed');
        return true;

    } catch (error) {
        console.error('Error during cleanup:', error);
        
        // Force reset critical states even if cleanup fails
        this.localAudioTrack = null;
        this.currentChannel = null;
        this.isInitialized = false;
        this.audioContext = null;
        this._isMuted = false;
        this.connectionState.status = 'disconnected'; //h
        
        // Attempt to force close any remaining resources
        try {
            if (window.iosAudioStream) {
                window.iosAudioStream.getTracks().forEach(track => track.stop());
                window.iosAudioStream = null;
            }
        } catch (finalError) {
            console.warn('Final cleanup attempt failed:', finalError);
        }

        return false;
    } finally {
        this.isCleaningUp = false;  // ADD: Reset flag in finally block
    }
}

// Core initialization
async initialize() {
    try {
        console.log('Starting Agora initialization...');
        if (this.isInitialized) {
            await this.cleanup();
        }

        // Request permissions explicitly
        const stream = await navigator.mediaDevices.getUserMedia({ 
            audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true
            }
        });
        stream.getTracks().forEach(track => track.stop());

        // Platform specific initialization
        if (this.isIOS) {
            await this.setupIOSAudio();
        }

        // Set up audio track
        const audioSetup = await this.verifyAudioTrack();
        if (!audioSetup.isValid) {
            throw new Error('Failed to setup audio: ' + JSON.stringify(audioSetup.details));
        }

        // Initialize client monitoring
        this.startClientMonitoring();
        this.connectionState.status = 'initialized';
        this.isInitialized = true;
        
        console.log('Agora initialization completed successfully');
        return true;

    } catch (error) {
        console.error('Initialize error:', error);
        this.isInitialized = false;
        this.connectionState.status = 'error';
        throw error;
    }
}

// Token management
async getToken(channelName) {
    try {
        const token = localStorage.getItem('authToken');
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/call/token`,
            { channelName },
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                timeout: 5000
            }
        );
        
        if (!response.data?.token) {
            throw new Error('Invalid token response');
        }
        
        return response.data.token;
    } catch (error) {
        console.error('Token fetch error:', error);
        throw new Error('Failed to get voice call token');
    }
}

isInChannel() {
    return this.client?.connectionState === 'CONNECTED' && 
           this.currentChannel !== null;
}

async verifyPeerConnection() {
    const stats = await this.client.getRTCStats();
    return {
        isValid: stats && !stats.RecvPacketLoss && !stats.SendPacketLoss,
        connectionQuality: stats?.UserCount > 0 ? 'connected' : 'no-peers',
        peerCount: stats?.UserCount || 0,
        networkQuality: stats?.RTT < 200 ? 'good' : 'poor'
    };
}

async verifyLocalTrack() {
    return {
        isValid: !!(this.localAudioTrack?.enabled && 
                    this.localAudioTrack?.getMediaStreamTrack()?.readyState === 'live'),
        state: this.localAudioTrack?.state || 'none',
        volume: await this.getLocalAudioLevel(),
        enabled: this.localAudioTrack?.enabled || false
    };
}

// Channel management
async join(channelName, existingToken = null) {
    try {
        console.log('Joining channel:', {
            channelName,
            hasExistingToken: !!existingToken,
            timestamp: new Date().toISOString()
        });

        // Initial validations remain same
        if (!this.appId) {
            throw new Error('Missing Agora App ID');
        }
        
        if (!channelName) {
            throw new Error('Channel name required');
        }

        // Check if already in same channel - keep existing logic
        if (this.isInChannel() && this.currentChannel === channelName) {
            const verificationResult = await this.verifyAudioTrack();
            if (verificationResult.isValid) {
                console.log('Already in channel with valid audio');
                return;
            }
            
            console.log('In channel but audio invalid, reconnecting');
            await this.client.leave();
        }

        // Ensure clean state before joining - keep existing logic
        if (this.client.connectionState !== 'DISCONNECTED') {
            console.log('Leaving existing channel before joining new one');
            await this.client.leave();
        }

        // Set channel and initialize if needed - keep existing logic
        this.currentChannel = channelName;
        
        if (!this.isInitialized) {
            await this.initialize();
        }

        // Modified token handling - Use existing token or get new one
        let token = existingToken;
        if (!token) {
            try {
                token = await this.getToken(channelName);
            } catch (error) {
                console.error('Token fetch failed, retrying once:', error);
                await new Promise(resolve => setTimeout(resolve, 1000));
                token = await this.getToken(channelName);
            }
        }

        if (!token) {
            throw new Error('Failed to get token');
        }

        // Rest of the function remains exactly the same
        await this.client.join(
            this.appId,
            channelName,
            token,
            null  // Use null for uid, let Agora assign
        );

        // Setup audio after joining
        const audioStatus = await this.setupLocalAudio();
        if (!audioStatus) {
            throw new Error('Failed to setup audio after join');
        }

        // Publish audio track immediately for two-way communication
        if (this.localAudioTrack) {
            await this.client.publish([this.localAudioTrack]);
            console.log('Published local audio track');
        }

        // Enable volume indicator for all users
        await this.client.enableAudioVolumeIndicator({
            interval: 200,  // Interval in ms
            smooth: 3,      // Smooth count
            enableVad: true // Voice activity detection
        });

        // Run diagnostics and log state
        const diagnosis = await this.diagnoseMissingAudio();
        console.log('Join complete - Audio diagnosis:', {
            channelName,
            hasExistingToken: !!existingToken,
            audioState: diagnosis,
            trackEnabled: this.localAudioTrack?.enabled,
            connectionState: this.client.connectionState
        });

        return true;

    } catch (error) {
        console.error('Join error:', {
            error: error.message,
            channelName,
            hasExistingToken: !!existingToken,
            timestamp: new Date().toISOString()
        });
        
        // Attempt cleanup on failure
        try {
            await this.leave();
        } catch (cleanupError) {
            console.error('Cleanup after join error failed:', cleanupError);
        }
        throw error;
    }
}

async verifyChannelState(channelName) {
    try {
        // Add delay for state sync
        await new Promise(resolve => setTimeout(resolve, 500));

        // Basic check first
        if (!this.isInChannel()) {
            // Try to recover basic connection
            if (this.client?.connectionState === 'DISCONNECTED') {
                console.log('Attempting to recover disconnected state');
                await this.join(channelName);
                // Wait for join to complete
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
            
            // Recheck after recovery attempt
            if (!this.isInChannel()) {
                return { 
                    verified: false, 
                    reason: 'Basic channel check failed',
                    recoverable: true,
                    details: {
                        connectionState: this.client?.connectionState,
                        currentChannel: this.currentChannel
                    }
                };
            }
        }

        // 1. Basic State Verification
        if (!this.client || !this.isInitialized) {
            return { 
                verified: false, 
                reason: 'Client not initialized',
                recoverable: false
            };
        }

        // 2. Connection State Verification
        const connectionState = this.client.connectionState;
        if (connectionState !== 'CONNECTED') {
            return { 
                verified: false, 
                reason: `Invalid connection state: ${connectionState}`,
                recoverable: connectionState !== 'DISCONNECTED',
                details: { connectionState }
            };
        }

        // 3. Channel Verification
        if (this.currentChannel !== channelName) {
            return { 
                verified: false, 
                reason: 'Channel name mismatch',
                recoverable: false,
                details: {
                    expected: channelName,
                    actual: this.currentChannel
                }
            };
        }

        // 4. Audio Track Verification
        const localTrackState = await this.verifyLocalTrack();
        if (!localTrackState.isValid) {
            // Try to recover audio track
            try {
                await this.setupLocalAudio();
                const retriedTrackState = await this.verifyLocalTrack();
                if (!retriedTrackState.isValid) {
                    return { 
                        verified: false, 
                        reason: 'Local track verification failed after recovery attempt',
                        recoverable: true,
                        details: retriedTrackState
                    };
                }
            } catch (audioError) {
                return { 
                    verified: false, 
                    reason: 'Local track verification failed',
                    recoverable: true,
                    details: localTrackState
                };
            }
        }

        // 5. Peer Connection Verification
        const peerState = await this.verifyPeerConnection();
        if (!peerState.isValid) {
            return {
                verified: false,
                reason: 'Peer connection verification failed',
                recoverable: true,
                details: peerState
            };
        }

        // Log successful verification
        console.log('Channel verification successful:', {
            channelName,
            connectionState,
            localTrackState,
            peerState,
            timestamp: new Date().toISOString()
        });

        return {
            verified: true,
            state: {
                connectionState,
                localTrack: localTrackState,
                peerConnection: peerState
            }
        };
    } catch (error) {
        console.error('Channel verification error:', {
            error: error.message,
            stack: error.stack,
            channelName,
            timestamp: new Date().toISOString()
        });
        return {
            verified: false,
            reason: 'Verification error',
            error: error.message,
            recoverable: true,
            details: {
                errorType: error.name,
                message: error.message
            }
        };
    }
}

// Audio controls
// In agoraService.js
// In agoraService.js
async toggleMute() {
    try {
        // 1. Verify & setup track if needed (keeping this from current)
        if (!this.localAudioTrack || this.localAudioTrack.state === 'ended') {
            await this.setupLocalAudio();
            await new Promise(resolve => setTimeout(resolve, 200));
            if (!this.localAudioTrack) {
                throw new Error('Failed to initialize audio track');
            }
        }

        // 2. Get current state
        const isMutedNow = this._isMuted;
        
        // 3. Toggle volume instead of enabled state
        if (isMutedNow) {
            await this.localAudioTrack.setVolume(100);
            await this.localAudioTrack.setEnabled(true);  // Keep track enabled
            this._isMuted = false;
        } else {
            await this.localAudioTrack.setVolume(0);
            this._isMuted = true;
        }

        // 4. Update audio state (keeping track of both volume and enabled)
        this.audioState = {
            ...this.audioState,
            volume: this._isMuted ? 0 : 100,
            isEnabled: true  // Track stays enabled
        };

        // 5. Verify state (keeping verification from current)
        const verifiedVolume = await this.localAudioTrack.getVolumeLevel();
        if ((this._isMuted && verifiedVolume > 0) || (!this._isMuted && verifiedVolume === 0)) {
            await new Promise(resolve => setTimeout(resolve, 100));
            await this.localAudioTrack.setVolume(this._isMuted ? 0 : 100);
        }

        return this._isMuted;
    } catch (error) {
        console.error('Mute toggle error:', error);
        // Keep error handling from current implementation
        const diagnosis = await this.diagnoseMissingAudio();
        return !diagnosis?.localTrack?.enabled;
    }
}

async handleDisconnection() {
    try {
        // Only attempt reconnection if not in cleanup
        if (this.client.connectionState === 'DISCONNECTED' && 
            this.currentChannel && 
            !this.isCleaningUp) {
            console.log('Handling disconnection for channel:', this.currentChannel);
            await new Promise(resolve => setTimeout(resolve, 1000));
            await this.join(this.channelName);
            this.connectionState.reconnectAttempts++;
        }
    } catch (error) {
        console.error('Failed to handle disconnection:', error);
    }
}

// Event handlers
async handleConnectionStateChange(curState, prevState) {
    console.log("Connection state:", prevState, "->", curState);
    this.connectionState.status = curState;
    
    if (curState === "DISCONNECTED") {
        // ADDED: Check if this is a cleanup disconnection
        if (!this.isCleaningUp && 
            this.connectionState.reconnectAttempts < this.connectionState.maxReconnectAttempts) {
            await this.handleDisconnection();
        } else {
            console.log('Skipping reconnection - cleanup in progress or max attempts reached');
            // Don't try to cleanup again if we're already cleaning up
            if (!this.isCleaningUp) {
                await this.cleanup();
            }
        }
    }
}

// Audio monitoring
monitorRemoteAudio(user, track) {
    // Clear existing monitor if any
    if (this._volumeMonitors.has(user.uid)) {
        clearInterval(this._volumeMonitors.get(user.uid));
    }

    // Store the user's UID for volume tracking
    this.remoteAudioLevels.set(user.uid, 0); // Initialize volume level

    // Cleanup when track is stopped
    track.once('stopped', () => {
        this._volumeMonitors.delete(user.uid);
        this.remoteAudioLevels.delete(user.uid);
    });
}

async handleUserPublished(user, mediaType) {
    try {
        console.log('Remote user published:', {
            userId: user.uid,
            mediaType,
            hasAudioTrack: !!user.audioTrack,
            timestamp: new Date().toISOString()
        });

        // First subscription attempt
        await this.client.subscribe(user, mediaType);

        if (mediaType === "audio") {
            let retryCount = 0;
            const maxRetries = 3;
            let remoteAudioTrack = user.audioTrack;

            // Retry loop for getting audio track
            while (!remoteAudioTrack && retryCount < maxRetries) {
                console.log(`Retry ${retryCount + 1}/${maxRetries} getting audio track for user ${user.uid}`);
                await new Promise(resolve => setTimeout(resolve, 1000));
                await this.client.subscribe(user, mediaType);
                remoteAudioTrack = user.audioTrack;
                retryCount++;
            }

            if (!remoteAudioTrack) {
                console.error('Remote audio track unavailable after retries:', {
                    userId: user.uid,
                    attempts: retryCount,
                    timestamp: new Date().toISOString()
                });
                return;
            }

            console.log(`Remote user ${user.uid} audio track received:`, {
                trackId: remoteAudioTrack.trackId,
                enabled: remoteAudioTrack.enabled,
                timestamp: new Date().toISOString()
            });

            try {
                // Set initial volume with verification
                await remoteAudioTrack.setVolume(100);
                
                // Verify volume setting
                const volumeLevel = await remoteAudioTrack.getVolumeLevel();
                console.log(`Initial volume set for user ${user.uid}:`, {
                    targetVolume: 100,
                    actualVolume: volumeLevel,
                    timestamp: new Date().toISOString()
                });

                // Play audio with explicit options
                await remoteAudioTrack.play(null, {
                    isAudioOnly: true,
                    playAudioTrack: true
                });

                // Start monitoring after successful play
                this.monitorRemoteAudio(user, remoteAudioTrack);

                console.log(`Successfully setup remote audio for user ${user.uid}`, {
                    trackId: remoteAudioTrack.trackId,
                    timestamp: new Date().toISOString()
                });
            } catch (audioError) {
                console.error('Error setting up remote audio:', {
                    error: audioError,
                    userId: user.uid,
                    trackId: remoteAudioTrack?.trackId,
                    timestamp: new Date().toISOString()
                });

                // Attempt recovery
                try {
                    await this.client.unsubscribe(user, mediaType);
                    await this.client.subscribe(user, mediaType);
                    await remoteAudioTrack.play(null, { isAudioOnly: true });
                } catch (recoveryError) {
                    console.error('Audio recovery failed:', {
                        error: recoveryError,
                        userId: user.uid,
                        timestamp: new Date().toISOString()
                    });
                }
            }
        }
    } catch (error) {
        console.error('Failed to handle remote user:', {
            error: error.message,
            stack: error.stack,
            userId: user?.uid,
            mediaType,
            timestamp: new Date().toISOString()
        });
    }
}



handleUserUnpublished(user, mediaType) {
    console.log(`Remote user ${user.uid} unpublished ${mediaType}`);
    if (mediaType === 'audio' && this._volumeMonitors.has(user.uid)) {
        clearInterval(this._volumeMonitors.get(user.uid));
        this._volumeMonitors.delete(user.uid);
    }
}

handleGenericError(error) {
    console.error('Agora generic error:', error);
    // Notify call service about unrecoverable Agora error
    if (this.onError) {
        this.onError({
            type: 'agora',
            message: error.message,
            recoverable: false
        });
    }
}

handleSecurityRestriction(error) {
    console.error('Security restriction:', error);
    this.handleGenericError(new Error('Security restriction: ' + error.message));
}

async handleAudioRecovery() {
    try {
        if (this.isCleaningUp || !this.client?.connectionState === 'CONNECTED') {
            console.log('Skipping audio recovery - not in valid state');
            return false;
        }

        console.log('Attempting Agora audio recovery...');
        
        // Retry audio track setup
        await this.setupLocalAudio();
        
        // Verify recovery
        const verification = await this.verifyAudioTrack();
        if (!verification.isValid) {
            throw new Error('Audio track verification failed after recovery');
        }

        return true;
    } catch (error) {
        console.error('Agora audio recovery failed:', error);
        return false;
    }
}

handleOperationAborted(error) {
    console.error('Operation aborted:', error);
    try {
        // Try to recover the operation
        this.handleAudioRecovery();
    } catch (recoveryError) {
        this.handleGenericError(recoveryError);
    }
}

handleInvalidOperation(error) {
    console.error('Invalid operation:', error);
    try {
        // Clean up current operation
        this.cleanup();
        throw new Error('Invalid operation: ' + error.message);
    } catch (cleanupError) {
        this.handleGenericError(cleanupError);
    }
}

// Error handling
handleError(error) {
    console.error('Agora error:', error);
    
    if (error.code) {
        switch (error.code) {
            case 'INVALID_OPERATION':
                this.handleInvalidOperation(error);
                break;
            case 'OPERATION_ABORTED':
                this.handleOperationAborted(error);
                break;
            case 'WEB_SECURITY_RESTRICT':
                this.handleSecurityRestriction(error);
                break;
            default:
                this.handleGenericError(error);
        }
    }
}

handleException(event) {
    console.warn("Agora exception:", event);
    this.handleError(event);
}

handleNetworkQuality(stats) {
    this.connectionState.networkQuality = stats;
    if (stats.uplinkNetworkQuality > 3 || stats.downlinkNetworkQuality > 3) {
        console.warn('Poor network quality detected:', stats);
    }
}

handleMediaReconnectStart(uid) {
    console.log(`Media reconnection started for uid: ${uid}`);
    this.connectionState.isReconnecting = true;
}

handleMediaReconnectEnd(uid) {
    console.log(`Media reconnection ended for uid: ${uid}`);
    this.connectionState.isReconnecting = false;
}

handleMediaRelayState(state, code) {
    console.log(`Media relay state: ${state}, code: ${code}`);
}

handleVolumeIndicator(volumes) {
    volumes.forEach((volume) => {
        console.log(`Volume indicator update - UID: ${volume.uid}, Level: ${volume.level}`);
        if (volume.uid === 0) { // Local user
            this.setLocalVolume(volume.level).catch(error => 
                console.error('Error updating local volume:', error)
            );
        } else { // Remote users
            this.setRemoteVolume(volume.uid, volume.level).catch(error => 
                console.error('Error updating remote volume:', error)
            );
        }
    });
}

// Initialize event listeners
setupEventListeners() {
    if (this.client) {
        this.client.on("connection-state-change", this.handleConnectionStateChange);
        this.client.on("user-published", this.handleUserPublished);
        this.client.on("user-unpublished", this.handleUserUnpublished);
        this.client.on("exception", this.handleException);

        // Additional monitoring
        this.client.on("network-quality", this.handleNetworkQuality.bind(this));
        this.client.on("media-reconnect-start", this.handleMediaReconnectStart.bind(this));
        this.client.on("media-reconnect-end", this.handleMediaReconnectEnd.bind(this));
        this.client.on("channel-media-relay-state", this.handleMediaRelayState.bind(this));
        this.client.on("volume-indicator", this.handleVolumeIndicator.bind(this));
    }
}

// Platform detection
checkIfIOS() {
    return [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod'
    ].includes(navigator.platform)
    || (navigator.userAgent.includes("Mac") && "ontouchend" in document);
}

}
// Create and export singleton instance
const agoraService = new AgoraService();
export default agoraService;